import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { SakuraField } from "../components/SakuraField";
import { HIRAGANA_STROKES, TRACE_PHASE_A_IDS } from "../lib/kana-strokes";
import {
  getStrokeMeta,
  pointsToSvgPath,
  samplePath,
  snapPointToPath,
  type Point,
} from "../lib/trace-judge";

const MAX_HEARTS = 5;
const VIEW_BOX = 109;

export function TracePage() {
  const { t } = useTranslation(["common", "learner"]);
  const navigate = useNavigate();

  const [lessonIds] = useState<string[]>(() => [...TRACE_PHASE_A_IDS]);
  const [index, setIndex] = useState(0);
  const currentChar = HIRAGANA_STROKES[lessonIds[index]] ?? null;

  const [currentStroke, setCurrentStroke] = useState(0);
  const [completedStrokes, setCompletedStrokes] = useState<Point[][]>([]);

  const svgRef = useRef<SVGSVGElement>(null);
  const isDrawingRef = useRef(false);
  const modelPointsRef = useRef<Point[]>([]);
  const lastSnapIdxRef = useRef(0);
  const [drawingPath, setDrawingPath] = useState<Point[]>([]);

  const [hearts] = useState(MAX_HEARTS);
  const [feedback, setFeedback] = useState<"ok" | "ng" | null>(null);

  // 文字変更時にリセット
  useEffect(() => {
    setCurrentStroke(0);
    setCompletedStrokes([]);
    setDrawingPath([]);
    setFeedback(null);
  }, [index]);

  // 書き順マーカーのメタ情報
  const currentStrokeMeta = useMemo(() => {
    if (!currentChar) return null;
    if (currentStroke >= currentChar.strokes.length) return null;
    return getStrokeMeta(currentChar.strokes[currentStroke]);
  }, [currentChar, currentStroke]);

  function toSVGPoint(e: React.PointerEvent<SVGSVGElement>): Point {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (feedback || !currentChar) return;
    // 現在の画の手本パスを点列化（描画開始時に一度だけ）
    modelPointsRef.current = samplePath(currentChar.strokes[currentStroke], 200);
    lastSnapIdxRef.current = 0;

    const p = toSVGPoint(e);
    const start = modelPointsRef.current[0];
    const startDist = Math.hypot(p.x - start.x, p.y - start.y);
    // 開始点から離れすぎている場合は描画開始しない（書き出しを正しい位置に促す）
    if (startDist > 30) return;

    isDrawingRef.current = true;
    setDrawingPath([start]);
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!isDrawingRef.current) return;
    const p = toSVGPoint(e);
    const { snapped, idx, advanced } = snapPointToPath(
      p,
      modelPointsRef.current,
      lastSnapIdxRef.current
    );
    if (advanced) {
      lastSnapIdxRef.current = idx;
      setDrawingPath((prev) => [...prev, snapped]);
    }
  }

  function onPointerUp() {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    if (!currentChar || feedback) {
      setDrawingPath([]);
      return;
    }
    const totalPoints = modelPointsRef.current.length;
    const reachedEnd = lastSnapIdxRef.current >= totalPoints - 8;
    const userPath = drawingPath;
    setDrawingPath([]);
    if (!reachedEnd) return; // 終端まで到達してない＝軌跡を消して再挑戦
    setCompletedStrokes((prev) => [...prev, userPath]);
    const nextStroke = currentStroke + 1;
    if (nextStroke >= currentChar.strokes.length) {
      setFeedback("ok");
    } else {
      setCurrentStroke(nextStroke);
    }
  }

  function next() {
    if (!currentChar) return;
    if (index + 1 < lessonIds.length) {
      setIndex(index + 1);
    } else {
      navigate("/");
    }
  }

  function skip() {
    if (!currentChar) return;
    setFeedback("ng");
  }

  if (!currentChar) {
    return (
      <div className="p-8 text-center text-muted text-xl">{t("common:loading")}</div>
    );
  }

  const progressPct = Math.round(((index + 1) / lessonIds.length) * 100);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-sakura/10 via-white to-blue-50">
      <SakuraField />

      {/* Top bar */}
      <header className="absolute top-0 left-0 right-0 z-30 p-3 sm:p-6">
        <div className="max-w-3xl mx-auto flex items-center gap-2 sm:gap-4">
          <Link
            to="/room"
            aria-label={t("learner:my_room")}
            className="bg-white/90 backdrop-blur rounded-full shadow-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-primary text-lg sm:text-xl font-bold hover:bg-white shrink-0"
          >
            ×
          </Link>
          <div className="flex-1 h-3 sm:h-4 bg-white/60 rounded-full overflow-hidden shadow-inner">
            <motion.div
              className="h-full bg-gradient-to-r from-sakura to-primary"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
          <div className="bg-white/90 backdrop-blur rounded-xl sm:rounded-2xl shadow-lg px-2.5 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 shrink-0">
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="#C04A4A"
              stroke="#C04A4A"
              strokeWidth="2"
            >
              <path
                d="M12 21s-7-4.5-9.5-9C0.5 8 3 4 6.5 4c2 0 3.5 1.5 5.5 3.5C14 5.5 15.5 4 17.5 4 21 4 23.5 8 21.5 12c-2.5 4.5-9.5 9-9.5 9z"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-base sm:text-lg font-bold text-accent">{hearts}</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative z-20 flex flex-col items-center justify-center h-full px-4 pt-20 sm:pt-24 pb-28 sm:pb-32">
        <p className="text-sm sm:text-lg lg:text-xl text-muted mb-3 sm:mb-5 text-center">
          {t("learner:trace_the_character")}
        </p>

        {/* 音声＋ローマ字＋手本 */}
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <button
            aria-label="play sound"
            tabIndex={-1}
            className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-sky-400 hover:bg-sky-500 text-white flex items-center justify-center shadow"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
            </svg>
          </button>
          <div className="flex flex-col items-center">
            <div className="text-[10px] sm:text-xs text-muted leading-none">
              {currentChar.romaji}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-primary leading-none mt-0.5">
              {currentChar.char}
            </div>
          </div>
        </div>

        {/* SVG キャンバス */}
        <motion.div
          key={`canvas-${index}`}
          className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-2 sm:p-4"
        >
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}
            className="block touch-none select-none cursor-crosshair"
            width="300"
            height="300"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <defs>
              <marker
                id="trace-arrow"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="4"
                markerHeight="4"
                orient="auto"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#3B82F6" />
              </marker>
            </defs>

            {/* 中央十字ガイド */}
            <line
              x1={VIEW_BOX / 2}
              y1={0}
              x2={VIEW_BOX / 2}
              y2={VIEW_BOX}
              stroke="#E5E7EB"
              strokeWidth="0.4"
              strokeDasharray="2 2"
            />
            <line
              x1={0}
              y1={VIEW_BOX / 2}
              x2={VIEW_BOX}
              y2={VIEW_BOX / 2}
              stroke="#E5E7EB"
              strokeWidth="0.4"
              strokeDasharray="2 2"
            />

            {/* 手本（全画、薄いグレー） */}
            {currentChar.strokes.map((d, i) => (
              <path
                key={`model-${i}`}
                d={d}
                stroke="#D1D5DB"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            ))}

            {/* 現在の画の経路ガイド（青い点線、Duolingo準拠） */}
            {!feedback &&
              currentStroke < currentChar.strokes.length &&
              drawingPath.length === 0 && (
                <path
                  d={currentChar.strokes[currentStroke]}
                  stroke="#3B82F6"
                  strokeWidth="3"
                  strokeDasharray="2.5 2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  opacity="0.7"
                />
              )}

            {/* 完了済み画（primary 色） */}
            {completedStrokes.map((stroke, i) => (
              <path
                key={`done-${i}`}
                d={pointsToSvgPath(stroke)}
                stroke="#2C3E50"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            ))}

            {/* 現在描画中の軌跡 */}
            {drawingPath.length > 0 && (
              <path
                d={pointsToSvgPath(drawingPath)}
                stroke="#3B82F6"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            )}

            {/* 書き順マーカー（現在の画の開始点・脈動・矢印） */}
            {!feedback && currentStrokeMeta && drawingPath.length === 0 && (
              <g>
                <circle
                  cx={currentStrokeMeta.start.x}
                  cy={currentStrokeMeta.start.y}
                  r="4"
                  fill="#3B82F6"
                />
                <circle
                  cx={currentStrokeMeta.start.x}
                  cy={currentStrokeMeta.start.y}
                  r="4"
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="1"
                  opacity="0.5"
                >
                  <animate
                    attributeName="r"
                    values="4;10;4"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.5;0;0.5"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </circle>
                <line
                  x1={currentStrokeMeta.start.x}
                  y1={currentStrokeMeta.start.y}
                  x2={currentStrokeMeta.start.x + currentStrokeMeta.direction.x * 14}
                  y2={currentStrokeMeta.start.y + currentStrokeMeta.direction.y * 14}
                  stroke="#3B82F6"
                  strokeWidth="2"
                  markerEnd="url(#trace-arrow)"
                />
              </g>
            )}
          </svg>
        </motion.div>
      </div>

      {/* Bottom bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-30 border-t transition-colors ${
          feedback === "ok"
            ? "border-green-200 bg-green-50/95"
            : feedback === "ng"
            ? "border-orange-200 bg-orange-50/95"
            : "border-gray-200 bg-white/95"
        } backdrop-blur`}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4 min-h-[76px] sm:min-h-[88px]">
          {feedback ? (
            <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
              <div
                className={`text-base sm:text-2xl font-bold ${
                  feedback === "ok" ? "text-green-700" : "text-orange-700"
                } flex items-center gap-2 sm:gap-3`}
              >
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full ${
                    feedback === "ok"
                      ? "bg-green-600 text-white"
                      : "bg-orange-500 text-white"
                  } text-xs sm:text-base shrink-0`}
                >
                  {feedback === "ok" ? "✓" : "✗"}
                </span>
                <span>
                  {feedback === "ok"
                    ? t("learner:correct")
                    : t("learner:incorrect")}
                </span>
                {feedback === "ng" && (
                  <span className="text-sm sm:text-base text-body font-normal">
                    {currentChar.char} ={" "}
                    <strong className="text-primary">{currentChar.romaji}</strong>
                  </span>
                )}
              </div>
              <button
                onClick={next}
                className={`font-bold rounded-xl sm:rounded-2xl px-6 sm:px-10 py-2.5 sm:py-3 shadow text-white text-sm sm:text-base shrink-0 ${
                  feedback === "ok"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-orange-500 hover:bg-orange-600"
                }`}
              >
                {t("learner:next")}
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={skip}
                className="border-2 border-gray-300 text-muted font-bold rounded-xl sm:rounded-2xl px-5 sm:px-8 py-2.5 sm:py-3 hover:bg-gray-50 text-sm sm:text-base"
              >
                SKIP
              </button>
              <button
                disabled
                aria-label="trace to complete"
                className="font-bold rounded-xl sm:rounded-2xl px-7 sm:px-10 py-2.5 sm:py-3 shadow bg-gray-200 text-gray-400 cursor-not-allowed text-sm sm:text-base"
              >
                CHECK
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
